

















































































import gql from 'graphql-tag'
import { Component, Mixins } from 'vue-property-decorator'

import AppAlertView from '@/components/AppAlertView.vue'
import AppCheckRound from '@/components/AppCheckRound.vue'
import AppEmptyCard from '@/components/AppEmptyCard.vue'
import AppOrderItemList from '@/components/AppOrderItemList.vue'
import AppRouterLink from '@/components/AppRouterLink.vue'
import AppTitleBar from '@/components/AppTitleBar.vue'
import AppTwoColsCard from '@/components/AppTwoColsCard.vue'
import ShoppingCartItem from '@/components/ShoppingCartItem.vue'
import { analytics } from '@/firebase'
import { CURRENT_USER } from '@/gql/auth'
import { SHOW_LOGIN_MODAL } from '@/gql/misc'
import { SHOPPING_CART_QUERY, REMOVE_SHOPPING_CART_ITEMS, CREATE_ORDER } from '@/gql/order'
import { CartItem } from '@/interfaces'
import { Common } from '@/mixins'

interface ShoppingCartGroup {
  seller: {
    id: string;
    shop: {
      deliveryFee: number;
      minForFreeDelivery: number;
    };
  };
  items: CartItem[];
}

@Component({
  components: {
    AppAlertView,
    AppCheckRound,
    AppEmptyCard,
    AppOrderItemList,
    AppRouterLink,
    AppTitleBar,
    AppTwoColsCard,
    ShoppingCartItem
  },
  apollo: {
    currentUser: {
      query: CURRENT_USER
    },
    getShoppingCart: {
      query: SHOPPING_CART_QUERY,
      result () {
        if (!Object.keys(this.cartItemsSelected).length) {
          this.selectOrDeselectAll()
        }
      }
    }
  }
})
export default class extends Mixins(Common) {
  currentUser = null
  getShoppingCart: ShoppingCartGroup[] = []
  cartItemsSelected: { [key: string]: boolean } = {}
  cartItemQuantities: { [key: string]: number } = {}
  isAlertViewEnabled = false

  cartItemById (cartItemId: string) {
    const group = this.getShoppingCart.find(group => group.items.find(item => item.id === cartItemId))
    if (group) {
      return group.items.find(item => item.id === cartItemId)
    } else {
      return undefined
    }
  }

  get sellerAmounts () {
    return this.getShoppingCart.reduce((accAmounts: { [key: string]: {
      deliveryFee: number;
      itemAmounts: number;
      remainedForFreeDelivery?: number;
    }; }, group) => {
      const selectedItems = group.items.filter(item => this.cartItemsSelected[item.id])

      const itemAmounts = selectedItems.reduce((acc, cur) =>
        acc + (cur.item.price + cur.itemVariant.additionalAmount) * this.cartItemQuantity(cur.id), 0)
      const deliveryFee = selectedItems.length === 0 ? 0
        : (group.seller.shop.minForFreeDelivery === 0
          ? group.seller.shop.deliveryFee
          : (group.seller.shop.minForFreeDelivery > itemAmounts ? group.seller.shop.deliveryFee : 0)
        )

      const remainedForFreeDelivery = group.seller.shop.minForFreeDelivery === 0 || itemAmounts >= group.seller.shop.minForFreeDelivery
        ? undefined
        : group.seller.shop.minForFreeDelivery - itemAmounts

      accAmounts[group.seller.id] = { itemAmounts, deliveryFee, remainedForFreeDelivery }

      return accAmounts
    }, {})
  }

  get totalItemAmounts () {
    return Object.keys(this.sellerAmounts).reduce((acc, cur) =>
      acc + this.sellerAmounts[cur].itemAmounts, 0)
  }

  get totalDeliveryFees () {
    return Object.keys(this.sellerAmounts).reduce((acc, cur) =>
      acc + this.sellerAmounts[cur].deliveryFee, 0)
  }

  get total () {
    return this.totalItemAmounts + this.totalDeliveryFees
  }

  cartItemQuantity (cartItemId: string) {
    if (this.cartItemQuantities[cartItemId]) {
      return this.cartItemQuantities[cartItemId]
    } else {
      const cartItem = this.cartItemById(cartItemId)
      if (cartItem) {
        return cartItem.quantity
      } else {
        return 0
      }
    }
  }

  get totalItemCount () {
    return this.getShoppingCart.reduce((acc, cur) => acc + cur.items.length, 0)
  }

  get selectedItemCount () {
    return Object.keys(this.cartItemsSelected).filter(id => this.cartItemsSelected[id]).length
  }

  selectOrDeselectAll () {
    if (this.selectedItemCount > 0) {
      this.cartItemsSelected = {}
    } else {
      this.getShoppingCart.forEach(group => {
        group.items.forEach(item => this.$set(this.cartItemsSelected, item.id, true))
      })
    }
  }

  toggleCartItemSelected (cartItemId: string) {
    this.$set(this.cartItemsSelected, cartItemId, !this.cartItemsSelected[cartItemId])
  }

  async updateQuantity (cartItemId: string, quantity: number) {
    this.$set(this.cartItemQuantities, cartItemId, quantity)

    const shoppingCartItemInputs = this.getShoppingCart.reduce((acc: CartItem[], cur) => {
      acc.push(...cur.items)
      return acc
    }, []).map(cartItem => ({
      shoppingCartItemId: cartItem.id,
      quantity: this.cartItemQuantity(cartItem.id)
    }))

    await this.$apollo.mutate({
      mutation: gql`mutation updateShoppingCartItems($input: UpdateShoppingCartItemInput!) {
        updateShoppingCartItems(input: $input) {
          success
        }
      }`,
      variables: {
        input: {
          shoppingCartItemInputs
        }
      }
    })
  }

  async increaseQuantity (cartItemId: string) {
    await this.updateQuantity(cartItemId, this.cartItemQuantity(cartItemId) + 1)
  }

  async decreaseQuantity (cartItemId: string) {
    await this.updateQuantity(cartItemId, this.cartItemQuantity(cartItemId) - 1)
  }

  async removeShoppingCartItems (shoppingCartItemIds: string[]) {
    const result = confirm('선택하신 상품을 장바구니에서 삭제하시겠습니까?')
    if (!result) {
      return
    }

    const { data } = await this.$apollo.mutate({
      mutation: REMOVE_SHOPPING_CART_ITEMS,
      variables: {
        input: {
          shoppingCartItemIds
        }
      }
    })

    await this.showToast(data.removeShoppingCartItems.message)
    await this.$apollo.queries.getShoppingCart.refetch()
  }

  async removeSelectedShoppingCartItems () {
    if (this.selectedItemCount === 0) {
      return
    }
    await this.removeShoppingCartItems(Object.keys(this.cartItemsSelected).filter(id => this.cartItemsSelected[id]))
  }

  async onCreateOrder () {
    if (!this.currentUser) {
      this.isAlertViewEnabled = true
    } else {
      await this.createOrder()
    }
  }

  async onActionAlertView (index: number) {
    this.isAlertViewEnabled = false

    if (index === 0) {
      await this.createOrder()
    } else if (index === 1) {
      await this.$apollo.mutate({ mutation: SHOW_LOGIN_MODAL })
    }
  }

  async createOrder () {
    const cartItems = Object.keys(this.cartItemsSelected)
      .filter(id => this.cartItemsSelected[id])
      .map(id => this.cartItemById(id))
      .reduce((acc: CartItem[], cur) => {
        if (cur) {
          acc.push(cur)
        }
        return acc
      }, [])

    if (cartItems.length === 0) {
      return
    }

    await this.showLoading()
    try {
      const { data } = await this.$apollo.mutate({
        mutation: CREATE_ORDER,
        variables: {
          input: {
            orderItemInputs: cartItems.map(cartItem => ({
              itemId: cartItem.item.id,
              itemVariantId: cartItem.itemVariant.id,
              quantity: this.cartItemQuantity(cartItem.id)
            }))
          }
        }
      })

      if (data.createOrder.success) {
        if (analytics) {
          await this.logAppsflyerEvent('af_initiated_checkout', {
            af_price: this.totalItemAmounts,
            af_content_id: cartItems.map(cartItem => cartItem.item.id),
            af_currency: 'KRW',
            af_quantity: cartItems.map(cartItem => this.cartItemQuantity(cartItem.id))
          })
        }
        await this.routerPush({
          path: `/orders/${data.createOrder.orderForm.id}/created`,
          query: { ids: cartItems.map(cartItem => cartItem.id) }
        })
      } else {
        await this.showToast(data.createOrder.message)
      }
    } finally {
      await this.dismissLoading()
    }
  }
}
